<script setup lang="ts">
import { TimeFormatType } from '@/enum'
import {
  DATE_FORMAT_TIME_PICKER,
  DEFAULT_JOB_END_TIME,
  DEFAULT_JOB_START_TIME,
  TIME_FORMAT_24_HRS
} from '@/utils/constants'
import moment from 'moment'
import { computed, onUnmounted, watch } from 'vue'
import FormDateTimePicker from './FormDateTimePicker.vue'
import FormTimePicker from './FormTimePicker.vue'

const boundValue = defineModel()
const props = defineProps<{
  startOfDay?: boolean
  endOfDay?: boolean
  timeFormat?: TimeFormatType
  label?: string
  labelDate?: string
  labelTime?: string
  disableFutureDates?: boolean
  defaultStartTime?: string
  disablePastTime?: boolean
  hideTime?: boolean
}>()

const selectedDate = computed({
  get: () => {
    if (boundValue.value) {
      const date = moment(boundValue.value).format(DATE_FORMAT_TIME_PICKER)
      return date
    }
    return ''
  },
  set: (newDate) => {
    if (!!newDate) {
      const date = moment(newDate).format(DATE_FORMAT_TIME_PICKER)
      const dateTime = moment(`${date} ${selectedTime.value}`).utc().format()
      boundValue.value = dateTime
    } else {
      boundValue.value = null
    }
  }
})

const selectedTime = computed({
  get: () => {
    if (boundValue.value) {
      const time = moment(boundValue.value).format(TIME_FORMAT_24_HRS)
      return time
    } else if (props.defaultStartTime) {
      return props.defaultStartTime
    } else if (props.startOfDay) {
      return DEFAULT_JOB_START_TIME
    } else if (props.endOfDay) {
      return DEFAULT_JOB_END_TIME
    }
    return ''
  },
  set: (newTime) => {
    const date = moment(selectedDate.value).format(DATE_FORMAT_TIME_PICKER)
    const dateTime = moment(`${date} ${newTime}`).toISOString()
    boundValue.value = dateTime
  }
})

const currentTime = computed(() => {
  if (props.disableFutureDates && selectedDate.value === moment().format(DATE_FORMAT_TIME_PICKER)) {
    return moment().format(TIME_FORMAT_24_HRS)
  }
  return ''
})

watch(boundValue, () => {
  const isFirstTimeAssignment = !boundValue.value
  if (isFirstTimeAssignment) {
    if (props.startOfDay) {
      selectedTime.value = DEFAULT_JOB_START_TIME
    } else if (props.endOfDay) {
      selectedTime.value = DEFAULT_JOB_END_TIME
    }
  }
})

onUnmounted(() => {
  boundValue.value = undefined
})
</script>
<template>
  <v-row no-gutters class="form-date-with-time-picker ga-3">
    <v-col>
      <FormDateTimePicker
        v-model="selectedDate"
        no-range
        no-actions
        :disable-past-dates="!disableFutureDates"
        :disable-future-dates
        small
        :label="labelDate ?? label"
        v-bind="$attrs"
      />
    </v-col>
    <v-col v-if="!hideTime" style="max-width: 130px">
      <FormTimePicker
        v-model="selectedTime"
        small
        :label="labelTime ?? 'Time'"
        :format="timeFormat"
        :maximum-start-time="currentTime"
        :default-start-time
        :disable-past-time
        :disabled="!selectedDate || !!$attrs.disabled"
        :readonly="!selectedDate || !!$attrs.readonly"
      />
    </v-col>
  </v-row>
</template>
<style scoped lang="scss">
.date-with-time-picker {
  flex: 1;
}
</style>
